package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/disposable_effect")
@Composable
fun DisposableEffectPage() {
    BlogLayout(
        PageProperties(
            "DisposableEffect",
            "" ),
        BlogProperties("Jetpack Compose", "DisposableEffect"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect and LaunchedEffect are executed when navigating between composables, especially focusing on their behavior when returning to a previously visited screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect is a Composable function that executes a side effect when its parent Composable is first rendered, and disposes of the effect when the Composable is removed from the UI hierarchy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It allows you to initialize imperative code as the composable enters the composition and clean it up as it leaves, preventing resource leaks and ensuring better performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoid Heavy Logic: Since `DisposableEffect` isn’t suspendable, avoid long-running tasks within it. For asynchronous operations, pair it with `LaunchedEffect`.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Keys Effectively: The dispatcher is used as a key to ensure dispose+restart if it changes. For spanning the same effect across recompositions instead of retriggering it you can pass a constant key: DisposableEffect(true) or DisposableEffect(Unit). DisposableEffect always requires at least 1 key. When the `DisposableEffect` key changes, the current effect is disposed of and a new one starts, so ensure your keys reflect the exact dependency you want to track.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect is used when a specific resource needs to be released if side effects are restarted (param/keys is changed) or compose is left (disappears from the screen).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect(key) restarts when the key changes — even if the composable stays. onDispose always runs before the new effect starts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Carefully manage the lifecycle of reactive streams, using functions like DisposableEffect for cleanup operations to prevent memory leaks",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Event listeners or callbacks that need to be unregistered. Subscriptions to data streams or observables that need to be disposed. Any resource that requires explicit cleanup, like timers or animations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Adding and removing event listeners",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Adding/removing callbacks",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Starting/stopping services",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Bind and unbinding sensors resources such as Camera, LocationManager, etc",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Managing database connections",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun ObserveLifecycleEvent(onEvent: (Lifecycle.Event) -> Unit = {}) {
                        val currentOnEvent by rememberUpdatedState(onEvent)
                        val lifecycleOwner = LocalLifecycleOwner.current

                        DisposableEffect(lifecycleOwner) {
                            val observer = LifecycleEventObserver { _, event ->
                                currentOnEvent(event)
                            }

                            lifecycleOwner.lifecycle.addObserver(observer)

                            onDispose {
                                lifecycleOwner.lifecycle.removeObserver(observer)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "When to Use DisposableEffect?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "`DisposableEffect` shines in scenarios where side effects must be tied closely to the lifecycle of a composable. Here are some common use cases:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Listeners or Callbacks: When attaching a listener to a system service, database, or UI component, `DisposableEffect` ensures that the listener is removed once the component is removed from the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Resource Management: Managing system resources like the camera, microphone, or location services. With `DisposableEffect`, you can release these resources precisely when the component is no longer active.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "API or Socket Connections: When working with WebSocket connections or API calls, `DisposableEffect` helps establish and close connections at the right times, ensuring they’re closed when the composable is out of use.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Location updates",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Media players",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Lifecycle of DisposableEffect",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "`DisposableEffect` is deeply integrated with the lifecycle of a composable. Here’s how it functions:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When the composable enters the composition, `DisposableEffect` runs its initialization code.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When the composable leaves the composition, the `onDispose` block is triggered, providing a precise point to release resources.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Execution Order: DisposableEffect, LaunchedEffect",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MyComposable(cartId: String) {
                        val lifecycleOwner = LocalLifecycleOwner.current

                        // DisposableEffect observes the lifecycleOwner
                        DisposableEffect(lifecycleOwner) {
                            Log.e("Init", "DisposableEffect")

                            onDispose {
                                Log.e("Init", "DisposableEffect - onDispose")
                            }
                        }

                        // LaunchedEffect triggers when cartId changes
                        LaunchedEffect(key1 = cartId) {
                            Log.e("Init", "LaunchedEffect")
                        }

                        // Scaffold is the UI container
                        Column {
                            Log.e("Init", "Column")
                            // You can add your screen content here
                        }
                    }

                    Output
                    E/Init: Column
                    E/Init: DisposableEffect
                    E/Init: LaunchedEffect

                    Composition starts
                        → Column() runs → logs "Column"
                        → Registers DisposableEffect block
                        → Registers LaunchedEffect block
                    Composition ends
                        → Side effects start
                        → DisposableEffect executes → logs "DisposableEffect"
                        → LaunchedEffect launches coroutine → logs "LaunchedEffect"
                    """.trimIndent()
            )

            Ol {
                //Composition Phase Comes First
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Jetpack Compose first builds the UI tree during composition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "At this point, Column is a composable function. It is executed immediately during the composition phase to build the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "So: Column() runs first → logs “Column”.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    //Side-Effects Are Registered During Composition But Executed After

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DisposableEffect and LaunchedEffect register their work during composition, but their actual execution happens after the composition finishes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose uses an internal scheduler (via Recomposer) to run side effects after the frame is committed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}